import { useMemo, useState } from 'react';
import { Phone, RefreshCw } from 'lucide-react';
import { translations } from '../../lib/i18n/translations';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { useVoiceCalls, type VoiceCall } from '../../lib/voice/hooks';
import { VoiceCallRow } from './VoiceCallRow';
import { VoiceCallTranscriptDrawer } from './VoiceCallTranscriptDrawer';
import { DialWithAiModal } from './DialWithAiModal';

interface Props {
  leadId: string;
  /** Se true mostra il bottone "Chiama con AI" (permesso voice_agents.dial). */
  canDial: boolean;
}

const MAX_CALLS = 5;

/**
 * Card scheda lead — estetica Notion (compatta) — con le ultime 5 chiamate AI del
 * lead e il bottone "Chiama con AI". Degrada graceful: se il backend voice non e'
 * disponibile useVoiceCalls setta error/lista vuota e la card resta silente.
 */
export function VoiceCallCard({ leadId, canDial }: Props) {
  const { settings } = useSettingsStore();
  const lang = settings?.language || 'en';
  const t = translations[lang].settings.voice;

  const { calls, loading, error, refetch } = useVoiceCalls();
  const [viewing, setViewing] = useState<VoiceCall | null>(null);
  const [dialOpen, setDialOpen] = useState(false);

  const leadCalls = useMemo(
    () => calls.filter((c) => c.lead_id === leadId).slice(0, MAX_CALLS),
    [calls, leadId],
  );

  // Sezione flat in stile Notion (header uppercase + azioni link), come le altre
  // sezioni della colonna sinistra: niente box bianco.
  return (
    <div className="text-[13px]">
      {/* Header sezione (come SectionHeader del layout) */}
      <div className="flex items-center justify-between py-2 mb-1">
        <div className="flex items-center space-x-2">
          <h3 className="text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wide">
            {t.dial.cardTitle}
          </h3>
          {leadCalls.length > 0 && (
            <span className="text-xs text-[#787774] dark:text-gray-500">({leadCalls.length})</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void refetch()}
            disabled={loading}
            className="p-1 text-[#787774] hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] rounded transition-colors disabled:opacity-50"
            title={t.calls.title}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {canDial && (
            <button
              onClick={() => setDialOpen(true)}
              className="text-xs text-[#337EA9] dark:text-[#529CCA] hover:underline flex items-center gap-1"
            >
              <Phone className="w-3 h-3" />
              {t.dial.button}
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      {error ? null : leadCalls.length === 0 ? (
        <div className="px-2 pb-1 text-[12px] text-[#787774] dark:text-gray-400">{t.dial.empty}</div>
      ) : (
        <ul className="divide-y divide-[rgba(55,53,47,0.09)] dark:divide-gray-700">
          {leadCalls.map((c) => (
            <VoiceCallRow key={c.id} t={t} lang={lang} call={c} onView={() => setViewing(c)} />
          ))}
        </ul>
      )}

      {viewing && (
        <VoiceCallTranscriptDrawer t={t} call={viewing} onClose={() => setViewing(null)} />
      )}
      {dialOpen && (
        <DialWithAiModal
          t={t}
          lang={lang}
          leadId={leadId}
          onClose={() => setDialOpen(false)}
          onDialed={() => void refetch()}
        />
      )}
    </div>
  );
}
